"use client";

import { useCallback, useState } from "react";

import { safeLocalStorage } from "@utilities/localStorage";

/**
 * localStorage-backed TTL hook. Reads a timestamp on mount and checks
 * whether it falls within `ttlDays`. Calling `markSuppressed()` persists
 * the current timestamp for future sessions without affecting the current one.
 * Pass `null` as key to disable suppression entirely.
 */
export function useLocalStorageTTL(key: string | null, ttlDays: number = 30) {
  const [isSuppressed] = useState(() => {
    if (key === null) return false;
    const raw = safeLocalStorage.getItem(key);
    if (raw === null) return false;
    const timestamp = parseInt(raw, 10);
    if (isNaN(timestamp)) return false;
    const daysSince = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
    return daysSince < ttlDays;
  });

  const markSuppressed = useCallback(() => {
    if (key !== null) safeLocalStorage.setItem(key, Date.now().toString());
  }, [key]);

  return [isSuppressed, markSuppressed] as const;
}
